import { Button, Col, DatePicker, Flex, Form, Row, Space, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axiosInstance from "../api";
import dayjs from "dayjs";
import { CSVLink } from "react-csv";
import { ExportOutlined, FileOutlined } from "@ant-design/icons";
import { getCsvSep } from "../util";

export default function () {
    const { orderId } = useParams();

    const [allocations, setAllocations] = useState([]);
    const [sp, setSP] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const startDate = sp.get("startDate") && dayjs(sp.get("startDate"));
    const endDate = sp.get("endDate") && dayjs(sp.get("endDate"));
    const visibleData = allocations.filter(
        (x) => !startDate || (startDate.isSameOrBefore(x.date) && endDate.isSameOrAfter(x.date)),
    );

    useEffect(() => {
        if (!!startDate && !!endDate) form.setFieldValue("range", [startDate, endDate]);
    }, [startDate, endDate]);

    useEffect(() => {
        setLoading(true);
        axiosInstance
            .get(`allocation`, { params: { order: orderId } })
            .then(({ data }) => {
                setAllocations(data.map((x) => ({ ...x, date: dayjs(x.date) })));
            })
            .finally(() => setLoading(false));
    }, []);

    const columns = [
        {
            key: "Data",
            dataIndex: "date",
            width: 100,
            title: "Data",
            defaultSortOrder: "descend",
            sorter: (a, b) => a.date.diff(b.date),
            render: (x) => x.format("DD/MM/YYYY"),
        },
        {
            key: "Personale",
            dataIndex: "employees",
            width: 400,
            title: "Personale",
            render: (data) => (
                <Flex gap={"2px 0"} wrap>
                    {data
                        .sort((a, b) => a.fullName.localeCompare(b.fullName))
                        .map((x) => (
                            <Tag key={x.id}>{x.fullName}</Tag>
                        ))}
                </Flex>
            ),
        },
        {
            key: "Mezzi",
            dataIndex: "vehicles",
            width: 400,
            title: "Mezzi",
            render: (data) => (
                <Flex gap={"2px 0"} wrap>
                    {data
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((x) => (
                            <Tag key={x.id}>{x.name}</Tag>
                        ))}
                </Flex>
            ),
        },
        {
            key: "manpower",
            width: 200,
            align: "right",
            title: "Ore manodopera stimate",
            render: (x) => x.employees.length * 8,
        },
    ];

    return (
        <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
            <Form
                layout="vertical"
                form={form}
                onFinish={(form) => {
                    if (form.range) {
                        form.startDate = form.range[0].format("YYYY-MM-DD");
                        form.endDate = form.range[1].format("YYYY-MM-DD");
                    }
                    delete form.range;
                    Object.entries(form).forEach(([k, v]) => sp.set(k, v));
                    setSP(sp, { replace: true });
                }}
            >
                <Row>
                    <Col flex={"auto"}>
                        <Space wrap>
                            <Form.Item
                                name={"range"}
                                label={"Data inizio e fine"}
                                style={{ margin: 0 }}
                            >
                                <DatePicker.RangePicker format={"DD/MM/YYYY"} />
                            </Form.Item>
                        </Space>
                    </Col>
                    <Col flex={"none"}>
                        <Space wrap>
                            <Form.Item style={{ margin: 0 }} label=" ">
                                <Button
                                    onClick={() => {
                                        ["startDate", "endDate", "includeMonth"].forEach((x) =>
                                            sp.delete(x),
                                        );
                                        form.resetFields();
                                        setSP(sp, { replace: true });
                                    }}
                                >
                                    Reset filtri
                                </Button>
                            </Form.Item>
                            <Form.Item style={{ margin: 0 }} label=" ">
                                <Button type="primary" htmlType="submit">
                                    Applica filtri
                                </Button>
                            </Form.Item>
                        </Space>
                    </Col>
                </Row>
            </Form>

            <CSVLink
                data={visibleData.map((x) => {
                    return {
                        Data: x.date.format("YYYY-MM-DD"),
                        Dipendenti: x.employees.map((x) => x.fullName).join(", "),
                        Mezzi: x.vehicles.map((x) => x.name).join(", "),
                        Responsabile: x.manager.fullName,
                        Note: x.notes,
                    };
                })}
                separator={getCsvSep()}
                filename={`allocazioni-${orderId}.csv`}
            >
                <Button icon={<ExportOutlined/>}>Esporta CSV</Button>
            </CSVLink>
            <Table
                size="small"
                loading={loading}
                rowKey="id"
                columns={columns}
                tableLayout={"fixed"}
                scroll={{ x: columns.reduce((acc, x) => acc + x.width, 0) }}
                pagination={{
                    showTotal: (total) => "Totale: " + total,
                    total: allocations.count ?? 0,
                }}
                dataSource={visibleData}
                summary={(data) => {
                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ fontWeight: "bold" }}>
                                <Table.Summary.Cell colSpan={2} index={0}>
                                    Totale
                                </Table.Summary.Cell>
                                <Table.Summary.Cell></Table.Summary.Cell>
                                <Table.Summary.Cell align="right">
                                    {visibleData.reduce(
                                        (acc, curr) => acc + curr.employees.length * 8,
                                        0,
                                    )}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        </div>
    );
}
